import { CountryCode } from '@glow/common'
import i18next from 'i18next'
import { List, Map, Seq, Set } from 'immutable'
import toLower from 'lodash-es/toLower'
import { MainAccessEntity } from '../domain/mainAccessType'
import { countriesForUpsales } from '../pages/admin/service-matrix-and-texts/utils'
import {
  Department,
  DepartmentGroup,
  DepartmentGroupIdType,
  DepartmentIdType,
  DepartmentWithNumberOfCourierAndPlanner,
  isParcelDepartment
} from '../types/coreEntitiesTypes'
import { AppStateType, ImmutableEntity } from '../utils/appStateReduxStore'
import {
  DepartmentGroupType,
  DepartmentType,
  DepartmentTypes,
  getDepartmentGroupId,
  getSelectedDepartmentIds,
  isGroupId
} from '../utils/departmentAndDepartmentGroupUtils'
import { createImmutableEqualSelector } from './createImmutableEqualSelector'

export const allDepartmentsFromStateSelector: (state: AppStateType) => Map<DepartmentIdType, Department> = (
  state: AppStateType
) => state.getIn(['entities', 'departments']) || Map()

export const allDepartmentIdsSelector: (state: AppStateType) => List<DepartmentIdType> = createImmutableEqualSelector(
  allDepartmentsFromStateSelector,
  (departments) => departments.keySeq().toList()
)

export const departmentsSelector: (state: AppStateType) => List<Department> = createImmutableEqualSelector(
  allDepartmentsFromStateSelector,
  (departments) => (departments ? List<Department>(departments.valueSeq()) : List<Department>())
)

export const allDepartmentsSelector = createImmutableEqualSelector(
  (state: AppStateType) => state.getIn(['entities', 'allDepartments']) as Map<string, ImmutableEntity>,
  (departments) =>
    departments ? List<Department>(departments.valueSeq() as Seq.Indexed<Department>) : List<Department>()
)

export const departmentsForUpsalesRulesAdminSelector = createImmutableEqualSelector(
  allDepartmentsSelector,
  (departments) =>
    departments.filter(
      (d) =>
        d.get('departmentType') === DepartmentType.HD &&
        countriesForUpsales.some((c) => c === d.get('country').toUpperCase())
    )
)

export const selectedDepartmentIdsSelector = createImmutableEqualSelector(
  (state: AppStateType) => state.getIn(['entities', 'departmentGroups']),
  (state: AppStateType, departmentOrGroupId?: string) => departmentOrGroupId,
  (allDepartmentGroups: Map<DepartmentGroupIdType, DepartmentGroup>, departmentId?: string) => {
    const props = { match: { params: { departmentId } } }
    const isDepartmentGroup = departmentId && isGroupId(departmentId)

    if (allDepartmentGroups) {
      return getSelectedDepartmentIds(allDepartmentGroups, props?.match.params.departmentId ?? '')
    }

    const numericId = !isDepartmentGroup && departmentId ? Number(departmentId) : Number.NaN
    return Number.isNaN(numericId) ? List<DepartmentIdType>() : List.of(numericId)
  }
)

export const selectedDepartmentIdsSetSelector = createImmutableEqualSelector(selectedDepartmentIdsSelector, (ids) =>
  ids.toSet()
)

export const selectedDepartmentsSelector: (state: AppStateType, departmentOrGroupId: string) => List<Department> =
  createImmutableEqualSelector(
    allDepartmentsFromStateSelector,
    selectedDepartmentIdsSelector,
    (departments, selectedDepartmentIds) => {
      const depIds = Set(selectedDepartmentIds)
      return departments
        .valueSeq()
        .filter((d) => depIds.contains(d.get('id')))
        .toList()
    }
  )

export const departmentGroupsSelector = (state: AppStateType): Map<DepartmentGroupIdType, DepartmentGroup> =>
  state.getIn(['entities', 'departmentGroups']) || Map<DepartmentGroupIdType, DepartmentGroup>()

export const departmentGroupListSelector = createImmutableEqualSelector(
  (state: AppStateType) =>
    (state.getIn(['entities', 'departmentGroups'])?.valueSeq() as List<DepartmentGroup>) || List<DepartmentGroup>(),
  (departmentGroups: List<DepartmentGroup>) => departmentGroups.sortBy((it) => it.get('name'))
)

// Department groups with their complete membership, as opposed to departmentGroupListSelector,
// which is trimmed to the departments the user is tagged to. Only the admin group pages read this.
export const allDepartmentGroupListSelector = createImmutableEqualSelector(
  (state: AppStateType) =>
    (state.getIn(['entities', MainAccessEntity.ALL_DEPARTMENT_GROUP])?.valueSeq() as List<DepartmentGroup>) ||
    List<DepartmentGroup>(),
  (departmentGroups: List<DepartmentGroup>) => departmentGroups.sortBy((it) => it.get('name'))
)

export const departmentGroupListForPreadviceSelector = createImmutableEqualSelector(
  departmentGroupListSelector,
  (departmentGroups: List<DepartmentGroup>) =>
    departmentGroups.filter((group) => group.get('type') === DepartmentGroupType.PREADVICE)
)

export const departmentGroupRegionListSelector = createImmutableEqualSelector(
  departmentGroupListSelector,
  (departmentGroups: List<DepartmentGroup>) =>
    departmentGroups.filter((group) => group.get('type') === DepartmentGroupType.REGION)
)

export const allDepartmentsInSameCountrySelector = createImmutableEqualSelector(
  (state: AppStateType) => state.getIn(['entities', 'sameCountryDepartments']) as Map<string, ImmutableEntity>,
  (departments) =>
    departments ? List<Department>(departments.valueSeq() as Seq.Indexed<Department>) : List<Department>()
)

export const departmentFromIdSelector: (state: AppStateType, departmentId: DepartmentIdType) => Department | undefined =
  createImmutableEqualSelector(
    allDepartmentsFromStateSelector,
    (state: AppStateType, departmentId: DepartmentIdType) => departmentId,
    (departmentsById, departmentId) => departmentsById.get(departmentId)
  )

export const departmentFromIdDefaultMapSelector: (state: AppStateType, departmentId: DepartmentIdType) => Department =
  createImmutableEqualSelector(
    allDepartmentsFromStateSelector,
    (state: AppStateType, departmentId: DepartmentIdType) => departmentId,
    (departmentsById, departmentId) => departmentsById.get(departmentId) ?? Map()
  )

export const departmentsOfTypeSelector: (
  state: AppStateType,
  departmentType: DepartmentType
) => Map<DepartmentIdType, Department> = createImmutableEqualSelector(
  allDepartmentsFromStateSelector,
  (state: AppStateType, departmentType: DepartmentType) => departmentType,
  (departments, departmentType) =>
    departments.filter((department) => department.get('departmentType') === departmentType)
)

export const departmentsFromIdsSelector: (
  state: AppStateType,
  departmentIds: List<DepartmentIdType>
) => Map<number, Department> = createImmutableEqualSelector(
  allDepartmentsFromStateSelector,
  (state: AppStateType, departmentIds: List<DepartmentIdType>) => departmentIds,
  (departmentsById: Map<number, Department>, departmentIds: List<DepartmentIdType>) =>
    departmentsById.filter((d) => departmentIds.includes(d.get('id'))) || List()
)

export const allSelectedAreParcelNorwayDepartmentsSelector: (
  state: AppStateType,
  selectedDepartmentIds: List<DepartmentIdType>
) => boolean = createImmutableEqualSelector(
  departmentsFromIdsSelector,
  (departments) =>
    departments.size > 0 &&
    departments.every(
      (department) =>
        department.get('departmentType') === DepartmentType.Parcel &&
        department.get('country')?.toUpperCase() === CountryCode.NO
    )
)

export const departmentsInSameCountrySelector: (state: AppStateType, countryCode: string) => List<Department> =
  createImmutableEqualSelector(
    allDepartmentsInSameCountrySelector,
    (state: AppStateType, countryCode: string) => countryCode,
    (departments, countryCode) => departments.filter((d) => toLower(d.get('country')) === toLower(countryCode))
  )

export const departmentNameSelector: (state: AppStateType, departmentOrGroupId: string) => string =
  createImmutableEqualSelector(
    allDepartmentsFromStateSelector,
    (state: AppStateType) =>
      (state.getIn(['entities', 'departmentGroups']) as List<DepartmentGroup>) || List<DepartmentGroup>(),
    (_state: AppStateType, departmentOrGroupId: string) => departmentOrGroupId,
    (departments, departmentGroups, departmentOrGroupId) => {
      if (isGroupId(departmentOrGroupId)) {
        const departmentGroupId = getDepartmentGroupId(departmentOrGroupId)

        return (
          departmentGroups.find((group) => group.get('id') === departmentGroupId)?.get('name') ?? departmentOrGroupId
        )
      } else {
        return (
          departments.find((department) => department.get('id') === Number(departmentOrGroupId))?.get('name') ??
          departmentOrGroupId
        )
      }
    }
  )

const departmentGroupCountByDepartmentId: (state: AppStateType) => Map<DepartmentIdType, number> =
  createImmutableEqualSelector(
    (state: AppStateType) => state.getIn(['entities', 'departmentGroups']) as Set<DepartmentGroup>,
    (depGroups) => getEntityCountGroupByDepartmentId(depGroups)
  )

export const departmentsWithNumberOfCourierAndPlannerSelector: (
  state: AppStateType
) => Map<DepartmentIdType, DepartmentWithNumberOfCourierAndPlanner> = createImmutableEqualSelector(
  (state: AppStateType) => state.getIn(['aggregates', 'userCount']) as Map<DepartmentIdType, number>,
  departmentGroupCountByDepartmentId,
  allDepartmentsFromStateSelector,
  (plannerDepartmentGroup, departmentGroupGroup, departments) => {
    return departments.map((department) => {
      const plannersForThisDepartment = plannerDepartmentGroup?.get(department.get('id'))
      const departmentGroupsForThisDepartment = departmentGroupGroup.get(department.get('id'))
      return department.withMutations((dept: any) => {
        dept
          .set('numberOfPlanners', plannersForThisDepartment || i18next.t('department.noPlanner'))
          .set('numberOfDepartmentGroups', departmentGroupsForThisDepartment || 0)
      }) as DepartmentWithNumberOfCourierAndPlanner
    })
  }
)

const getEntityCountGroupByDepartmentId = <T extends Map<any, any>>(entity: Set<T>): Map<DepartmentIdType, number> => {
  if (!entity) return Map()
  const groupByDepartmentIds = entity.groupBy((grouper: T) => grouper.get('departments'))
  let entityCountObject: Map<DepartmentIdType, number> = Map()
  groupByDepartmentIds.map((entityMap, listOfDepartmentIds: List<DepartmentIdType>) => {
    listOfDepartmentIds.map((departmentId) => {
      entityCountObject = entityCountObject.withMutations((mutator) => {
        mutator.set(departmentId, entityMap.count() + (entityCountObject.get(departmentId) || 0))
      })
    })
  })
  return entityCountObject
}

export const allSelectedAreParcelDepartmentsSelector: (state: AppStateType, departmentOrGroupId?: string) => boolean =
  createImmutableEqualSelector(
    allDepartmentsFromStateSelector,
    selectedDepartmentIdsSelector,
    (departments, selectedDepartmentIds) => {
      const selectedDepartments = departments.filter((d) => selectedDepartmentIds.contains(d.get('id')))
      return (
        selectedDepartments.size > 0 &&
        selectedDepartments.every((department) => department.get('departmentType') === DepartmentType.Parcel)
      )
    }
  )

export const anySelectedIsParcelDepartmentSelector: (state: AppStateType, departmentOrGroupId?: string) => boolean =
  createImmutableEqualSelector(allDepartmentsFromStateSelector, selectedDepartmentIdsSelector, (departments, ids) =>
    ids.some((id) => isParcelDepartment(departments.get(id)))
  )

export const areDepartmentsEligibleForArrivalStatusSelector: (
  state: AppStateType,
  departmentOrGroupId?: string
) => boolean = createImmutableEqualSelector(
  allDepartmentsFromStateSelector,
  selectedDepartmentIdsSelector,
  (departments, selectedDepartmentIds) => {
    const eligibleDepartments: DepartmentTypes[] = ['courier', 'hd', 'parcel']
    const selectedDepartments = departments.filter((d) => selectedDepartmentIds.contains(d.get('id')))
    return (
      selectedDepartments.size > 0 &&
      selectedDepartments.every((department) => eligibleDepartments.includes(department.get('departmentType')))
    )
  }
)
